// Organisation → Access: which dashboard widgets a role gets, and whose data
// they add up (20261015220000_dashboard.sql). org:full writes both; the role
// card's fieldset is disabled for anyone else, and RLS refuses them anyway.
// Raising — a widget on, a wider scope — asks first (UI-CONVENTIONS §5, rung 2:
// access); lowering saves at once, with a receipt.
import { useEffect, useReducer, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { write, type WriteResult } from '../lib/write';
import { SCOPE_LABEL, SCOPES, type DashboardScope } from '../lib/dashboard';
import { Checkbox, Select } from './primitives';
import { Eyebrow } from './card';
import { ConfirmDialog } from './dialog';
import { LoadError, useToast } from './feedback';

type Widget = { key: string; label: string; description: string };
type State = { roleId: string; catalog: Widget[]; on: string[]; scope: DashboardScope };

const RANK: Record<DashboardScope, number> = { own: 0, chain: 1, studio_no_money: 2, studio: 3 };
// Neither is studio staff: the server never gives them a dashboard.
const NO_DASHBOARD = new Set(['client', 'agent']);

const note = {
  fontSize: 'var(--hf-text-xs)',
  color: 'var(--hf-ink-3)',
  lineHeight: 'var(--hf-leading-body)',
} as const;

export function DashboardAccess({ roleId, roleLabel }: { roleId: string; roleLabel: string }) {
  const { push } = useToast();
  const [loaded, setState] = useState<State | { roleId: string; failed: true } | null>(null);
  // Another role's answer is no answer: this one is still loading.
  const state = loaded?.roleId === roleId ? loaded : null;
  const [tries, retry] = useReducer((n: number) => n + 1, 0);
  const [ask, setAsk] = useState<{
    title: string;
    consequence: string;
    confirmLabel: string;
    run: () => void;
  } | null>(null);

  useEffect(() => {
    if (!supabase || NO_DASHBOARD.has(roleId)) return;
    const sb = supabase;
    let live = true;
    void Promise.all([
      sb.from('dashboard_widgets').select('key, label, description').order('position'),
      sb.from('role_dashboard_widgets').select('widget').eq('role_id', roleId),
      sb.from('roles').select('dashboard_scope').eq('id', roleId).maybeSingle(),
    ]).then(([cat, rows, role]) => {
      if (!live) return;
      if (cat.error || rows.error || role.error || !role.data)
        return setState({ roleId, failed: true });
      setState({
        roleId,
        catalog: (cat.data as Widget[]) ?? [],
        on: ((rows.data as { widget: string }[]) ?? []).map((r) => r.widget),
        scope: (role.data as { dashboard_scope: DashboardScope }).dashboard_scope,
      });
    });
    return () => {
      live = false;
    };
  }, [roleId, tries]);

  if (NO_DASHBOARD.has(roleId))
    return (
      <Section>
        <div style={note}>This role never gets a dashboard: it isn’t studio staff.</div>
      </Section>
    );
  if (state && 'failed' in state)
    return (
      <Section>
        <LoadError what={`${roleLabel}’s dashboard`} onRetry={retry} />
      </Section>
    );
  if (!state || !supabase)
    return (
      <Section>
        <div style={note}>Loading…</div>
      </Section>
    );
  const sb = supabase;

  const report = (r: WriteResult, what: string, rollback: () => void) => {
    if (r.ok) return push({ tone: 'success', message: `${roleLabel}’s dashboard: ${what}.` });
    rollback();
    push({
      tone: 'error',
      message: `${roleLabel}’s dashboard wasn’t saved: ${r.reason === 'refused' ? 'your role can’t change this.' : r.error.message}`,
    });
  };
  const setOn = (on: string[]) => setState((s) => (s && !('failed' in s) ? { ...s, on } : s));

  const toggle = (w: Widget, turnOn: boolean) => {
    const was = state.on;
    const run = async () => {
      setOn(turnOn ? [...was, w.key] : was.filter((k) => k !== w.key));
      const q = turnOn
        ? sb.from('role_dashboard_widgets').insert({ role_id: roleId, widget: w.key })
        : sb.from('role_dashboard_widgets').delete().eq('role_id', roleId).eq('widget', w.key);
      report(await write(q, 'role_id'), `${w.label} ${turnOn ? 'on' : 'off'}`, () => setOn(was));
    };
    if (!turnOn) return void run();
    setAsk({
      title: `Show ${w.label} to ${roleLabel}?`,
      consequence: `Everyone with the ${roleLabel} role will see it on their dashboard, for ${SCOPE_LABEL[state.scope].toLowerCase()}. ${w.description}`,
      confirmLabel: `Show ${w.label}`,
      run: () => void run(),
    });
  };

  const setScope = (scope: DashboardScope) => {
    const was = state.scope;
    if (scope === was) return;
    const run = async () => {
      setState((s) => (s && !('failed' in s) ? { ...s, scope } : s));
      report(
        await write(sb.from('roles').update({ dashboard_scope: scope }).eq('id', roleId)),
        `now ${SCOPE_LABEL[scope].toLowerCase()}`,
        () => setState((s) => (s && !('failed' in s) ? { ...s, scope: was } : s)),
      );
    };
    // Money back on a studio-wide view is a raise too.
    if (RANK[scope] <= RANK[was]) return void run();
    setAsk({
      title: `Widen ${roleLabel}’s dashboard?`,
      consequence: `Everyone with the ${roleLabel} role will see figures for ${SCOPE_LABEL[scope].toLowerCase()}, not ${SCOPE_LABEL[was].toLowerCase()}.`,
      confirmLabel: 'Widen it',
      run: () => void run(),
    });
  };

  return (
    <Section>
      <div style={{ ...note, marginBottom: 'var(--hf-space-3)' }}>
        Shown while this role has Dashboard access. Deal values only ever reach a project’s manager
        and Pipeline: Full.
      </div>
      <div style={{ width: 'min(100%, 20rem)', marginBottom: 'var(--hf-space-3)' }}>
        <Select
          ariaLabel={`Whose figures ${roleLabel}’s dashboard shows`}
          value={state.scope}
          options={SCOPES.map((s) => ({ value: s, label: SCOPE_LABEL[s] }))}
          onChange={(v) => setScope(v as DashboardScope)}
        />
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {state.catalog.map((w) => (
          <li
            key={w.key}
            style={{ padding: 'var(--hf-space-2) 0', borderTop: '1px solid var(--hf-rule)' }}
          >
            <Checkbox
              label={w.label}
              checked={state.on.includes(w.key)}
              onChange={(v) => toggle(w, v)}
            />
            <div style={{ ...note, paddingLeft: 'var(--hf-space-6)' }}>{w.description}</div>
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={!!ask}
        title={ask?.title ?? ''}
        consequence={ask?.consequence ?? ''}
        confirmLabel={ask?.confirmLabel ?? ''}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          ask?.run();
          setAsk(null);
        }}
      />
    </Section>
  );
}

const Section = ({ children }: { children: ReactNode }) => (
  <div style={{ marginTop: 'var(--hf-space-4)' }}>
    <Eyebrow as="h4" style={{ padding: 'var(--hf-space-1) 0' }}>
      Dashboard
    </Eyebrow>
    {children}
  </div>
);
